import { useMutation, useQueryClient } from "@tanstack/react-query";
import { Link, useParams } from "react-router";

import { ApiError, api } from "../lib/api";
import { keys } from "../lib/queries";
import { useTitle } from "../lib/useTitle";

// Both pages act on a button click only: link scanners in mail filters open URLs and
// must not confirm or remove an address on their own.

export function RecipientConfirm() {
  useTitle("Confirmation du destinataire");
  const { token = "" } = useParams();
  const client = useQueryClient();
  const confirm = useMutation({
    mutationFn: () => api.confirmRecipient(token),
    // The owner may be signed in on this browser.
    onSuccess: () => client.invalidateQueries({ queryKey: keys.recipients }),
  });

  if (confirm.isError && confirm.error instanceof ApiError && [400, 404].includes(confirm.error.status)) {
    return (
      <div className="page page--narrow">
        <div className="centered-state">
          <h1>Ce lien de confirmation n'est pas valide</h1>
          <p>{confirm.error.message} Vérifiez que vous avez copié le lien en entier.</p>
          <div className="actions">
            <Link className="button" to="/">
              Retour à l'accueil
            </Link>
          </div>
        </div>
      </div>
    );
  }
  if (confirm.isSuccess) {
    return (
      <div className="page page--narrow">
        <div className="centered-state" role="status">
          <h1>Adresse confirmée</h1>
          <p>
            <strong>{confirm.data.email}</strong> recevra désormais les alertes. Chaque e-mail contient un lien pour
            ne plus les recevoir.
          </p>
        </div>
      </div>
    );
  }
  return (
    <div className="page page--narrow">
      <div className="centered-state">
        <h1>Recevoir ces alertes par e-mail ?</h1>
        <p>
          Un utilisateur a ajouté votre adresse aux destinataires de ses alertes sur les entreprises qu'il suit.
          Confirmez pour commencer à les recevoir ; sinon, ignorez simplement ce lien.
        </p>
        {confirm.isError && (
          <div className="notice notice--error" role="alert">
            {confirm.error.message}
          </div>
        )}
        <div className="actions">
          <button type="button" className="button" disabled={confirm.isPending} onClick={() => confirm.mutate()}>
            {confirm.isPending ? "Confirmation en cours" : "Confirmer mon adresse"}
          </button>
        </div>
      </div>
    </div>
  );
}

export function RecipientRemoval() {
  useTitle("Ne plus recevoir les alertes");
  const { token = "" } = useParams();
  const leave = useMutation({ mutationFn: () => api.leaveRecipients(token) });

  if (leave.isSuccess) {
    return (
      <div className="page page--narrow">
        <div className="centered-state" role="status">
          <h1>Vous ne recevrez plus ces alertes</h1>
          <p>{leave.data.message}</p>
          <div className="actions">
            <Link className="button button--quiet" to="/">
              Retour à l'accueil
            </Link>
          </div>
        </div>
      </div>
    );
  }
  return (
    <div className="page page--narrow">
      <div className="centered-state">
        <h1>Ne plus recevoir ces alertes ?</h1>
        <p>
          Votre adresse sera retirée des destinataires. La personne qui vous a ajouté continuera de recevoir ses
          propres alertes.
        </p>
        {leave.isError && (
          <div className="notice notice--error" role="alert">
            {leave.error.message}
          </div>
        )}
        <div className="actions">
          <button type="button" className="button" disabled={leave.isPending} onClick={() => leave.mutate()}>
            Ne plus recevoir les alertes
          </button>
          <Link className="button button--quiet" to="/">
            Continuer à les recevoir
          </Link>
        </div>
      </div>
    </div>
  );
}
